import React from 'react';
import {
  Form,
  Flex,
  Button,
  Checkbox,
  Space,
  Drawer,
  Avatar,
  Card,
  Input,
  Typography,
  Spin,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

// Components
import InputFormBox from 'components/bank/AGBBank/InputFormBox';
import NoComponent from 'components/bank/AGBBank/NoComponent';
import DynamicForm from 'components/ossix/DynamicForm';
import AccountDropdown from 'components/shared/AccountDropdown';
import ContactDropdown, {
  ImageWithFallback,
} from 'components/shared/ContactDropdown';
import OssixProvidersDropdown from 'components/shared/OssixProvidersDropdown';
import TransferConfirmation from 'components/shared/TransferConfirmation';

import { useLanguage } from 'hooks/Language/useLanguage';
import { maxSize } from 'constants/screenSize';
import agbColors from 'constants/Colors/agbColors';

import { ITransferForm } from 'interfaces/components/pageProps';
import { LocationState } from 'interfaces/otherInterface/dynamicInput';
import { IConfirmationData } from 'interfaces/queries/transactions';
import { ActionType, TargetType } from 'types/generalType';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';

const { Text } = Typography;

const TransferToMNOPage: React.FC<ITransferForm> = ({
  form,
  step,
  onPrevious,
  accountNumber,
  setAccountNumber,
  loadingAccount,
  selectAccountNumber,
  onHandleDisplaySelectContact,
  dynamicFormItem,
  displaySelectContact,
  recipientAccountNumber,
  contactAccount,
  setContactAccount,
  isLoadingContacts,
  onHandleCloseSelectContact,
  userInfo,
  isDrawerOpen,
  handleCloseOpenDrawer,
}) => {
  const { translate } = useLanguage();
  const location = useLocation();
  const state = location.state as LocationState;

  const {
    meta,
    fields,
    transferConfirmationData,
    onPINValuesChange,
    onHandleConfirmTransfer,
    loadSendFundsToGateway,
    selectedBeneficiary,
    selectedProvider,
    setSelectedProvider,
    handleInputChange,
    filterCountry,
    setSelectedBeneficiary,
    isVerifyAccountSuccess,
    loadVerifyAccount,
    onHandleVerifyAccount,
    onHandleTransferConfirmation,
    loadTransferConfirmation,
    provider,
    fixedInputValue,
    setFixedInputValue,
    onChangeFixedInputValue,
    onHandleSaveContact,
    formData,
  } = dynamicFormItem;

  // Convert confirmation data for display
  const convertConfirmationData = (data: IConfirmationData) => {
    return [
      { label: 'Amount to be sent', value: data?.Amount },
      { label: 'Fees', value: data?.Fees },
      { label: 'Exchange fees', value: data?.ExchangeFees },
      { label: 'Exchange rate', value: data?.ExchangeRate },
      { label: 'Taxes', value: data?.Taxes },
      { label: 'Total amount', value: data?.TotalAmount },
    ];
  };

  const confirmationItems = convertConfirmationData(
    transferConfirmationData,
  );

  const drawerStyles: DrawerStyles = {
    header: {
      background: agbColors.navyBlueColor,
      color: agbColors.lightBlueColor,
      padding: '16px 24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      height: '64px',
    },
    body: {
      padding: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    content: {
      flex: 1,
      overflowY: 'hidden',
      padding: '0',
    },
    mask: {
      background: 'rgba(0, 0, 0, 0.45)',
    },
  };

  const renderStep1Actions = () => (
    <div className="drawer-footer">
      {!selectedBeneficiary &&
        !isVerifyAccountSuccess &&
        meta?.verifyAccount && (
          <Button
            type="primary"
            onClick={onHandleVerifyAccount}
            loading={loadVerifyAccount}
            size="large"
            block
          >
            {translate('Next')}
          </Button>
        )}
      {selectedBeneficiary && (
        <Button
          type="primary"
          onClick={onHandleTransferConfirmation}
          loading={loadTransferConfirmation}
          size="large"
          block
        >
          {translate('Proceed')}
        </Button>
      )}

      {isVerifyAccountSuccess && (
        <Button
          type="primary"
          size="large"
          block
          onClick={onHandleTransferConfirmation}
          loading={loadTransferConfirmation}
          disabled={
            !accountNumber?.value || !fixedInputValue ? true : false
          }
        >
          {translate('Proceed')}
        </Button>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="drawer-scrollable-content">
            <InputFormBox
              displaySelectRecipient={!displaySelectContact}
              title={state?.chosenOption}
              onSelectRecipient={onHandleDisplaySelectContact}
              select={[
                <AccountDropdown
                  account={accountNumber}
                  loading={loadingAccount}
                  options={selectAccountNumber}
                  setAccount={setAccountNumber}
                  size="100%"
                />,
              ]}
              formItems={[
                <Form
                  form={form}
                  layout="vertical"
                  key="form"
                  initialValues={formData}
                  className="transfer-form"
                >
                  {displaySelectContact && (
                    <div className="beneficiary-selection-container">
                      <Space.Compact style={{ width: '100%' }}>
                        <Form.Item
                          label={
                            <Text strong>
                              {translate('Select a beneficiary')}
                            </Text>
                          }
                          className="beneficiary-dropdown"
                          name="recipient"
                          style={{ width: '100%' }}
                        >
                          <ContactDropdown
                            contact={contactAccount}
                            setContact={setContactAccount}
                            loading={isLoadingContacts}
                            options={recipientAccountNumber}
                          />
                        </Form.Item>
                        <Form.Item label=" ">
                          <Button
                            size="large"
                            type="primary"
                            ghost
                            onClick={onHandleCloseSelectContact}
                            icon={<CloseOutlined />}
                          />
                        </Form.Item>
                      </Space.Compact>
                    </div>
                  )}

                  {selectedBeneficiary && (
                    <Card
                      size="small"
                      className="beneficiary-card"
                      style={{
                        background:
                          agbColors.$lightNayBlueColor10Percent,
                        width: '100%',
                        maxWidth: maxSize,
                        marginBottom: '20px',
                      }}
                    >
                      <Flex gap="small" align="center">
                        <Avatar
                          size={48}
                          shape="square"
                          src={
                            <ImageWithFallback
                              src={selectedBeneficiary?.Logo}
                              firstName={
                                selectedBeneficiary?.FirstName
                              }
                              lastName={selectedBeneficiary?.LastName}
                            />
                          }
                          className="beneficiary-avatar"
                        />
                        <Flex vertical>
                          <Flex gap="small" align="center">
                            <Text strong>{translate('Name')}:</Text>
                            <Text>
                              {selectedBeneficiary?.FirstName}{' '}
                              {selectedBeneficiary?.LastName}
                            </Text>
                          </Flex>
                          <Flex gap="small" align="center">
                            <Text strong>
                              {translate('Account Number')}:
                            </Text>
                            <Text>
                              {selectedBeneficiary?.AccountNumber}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  )}

                  {!selectedBeneficiary && (
                    <Form.Item
                      label={
                        <Text strong>
                          {translate('Select operator')}
                        </Text>
                      }
                      className="operator-dropdown"
                    >
                      <OssixProvidersDropdown
                        style={{ width: '100%' }}
                        getSelectedProvider={setSelectedProvider}
                        selectedProvider={selectedProvider}
                        beneficiaryData={selectedBeneficiary}
                        setSelectedBeneficiary={
                          setSelectedBeneficiary
                        }
                        onCloseSelectContactDropdown={
                          onHandleCloseSelectContact
                        }
                        payload={{
                          ActionType: ActionType.Transfer,
                          CountryCode:
                            userInfo?.[0]?.OperationCountry,
                          TargetType: TargetType.MNOGetaway,
                        }}
                        showDefaultValue
                      />
                    </Form.Item>
                  )}

                  {!selectedBeneficiary && (
                    <div className="dynamic-form-container">
                      <DynamicForm
                        meta={meta}
                        form={form}
                        provider={provider}
                        inputs={fields || []}
                        amountRequired={true}
                        verifyAccount={meta?.verifyAccount}
                        handleInputChange={handleInputChange}
                        countries={filterCountry}
                        fixedInputValue={fixedInputValue}
                        setFixedInputValue={setFixedInputValue}
                        onChangeFixedInputValue={
                          onChangeFixedInputValue
                        }
                        defaultCountry={
                          userInfo?.[0]?.OperationCountry
                        }
                        isVerifyAccountSuccess={
                          isVerifyAccountSuccess
                        }
                      />
                    </div>
                  )}

                  {selectedBeneficiary && (
                    <Form.Item
                      name="Amount"
                      label={
                        <Text strong>{translate('Amount')}</Text>
                      }
                      className="amount-input"
                    >
                      <Input
                        type="number"
                        size="large"
                        name="Amount"
                        placeholder={translate(
                          'Enter the amount here',
                        )}
                        min={0}
                        step="0.01"
                      />
                    </Form.Item>
                  )}

                  {isVerifyAccountSuccess &&
                    !displaySelectContact && (
                      <Form.Item className="save-contact-option">
                        <Checkbox onChange={onHandleSaveContact}>
                          {translate('Save Phone Number')}
                        </Checkbox>
                      </Form.Item>
                    )}
                </Form>,
              ]}
            />
          </div>
        );

      case 2:
        return (
          <div className="drawer-scrollable-content">
            <TransferConfirmation
              isLoading={loadSendFundsToGateway}
              items={confirmationItems}
              onHandleBack={onPrevious}
              onHandleConfirm={onHandleConfirmTransfer}
              onValuesChange={onPINValuesChange}
              hideButtons={true}
            />
          </div>
        );

      default:
        return (
          <div className="empty-state">
            <NoComponent />
          </div>
        );
    }
  };

  const renderFooter = () => {
    switch (step) {
      case 1:
        return renderStep1Actions();
      case 2:
        return (
          <div className="drawer-footer dual-button">
            <Button
              variant="filled"
              color="volcano"
              type="primary"
              onClick={onPrevious}
              size="large"
            >
              {translate('Back')}
            </Button>
            <Button
              type="primary"
              onClick={onHandleConfirmTransfer}
              loading={loadSendFundsToGateway}
              size="large"
            >
              {translate('Confirm')}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Drawer
      width={500}
      open={isDrawerOpen?.open}
      title={
        <Text
          style={{ color: 'white', fontSize: 16, fontWeight: 500 }}
        >
          {translate(isDrawerOpen?.drawerTitle)}
        </Text>
      }
      styles={drawerStyles}
      closeIcon={false}
      onClose={handleCloseOpenDrawer}
      className="transfer-mno-drawer"
      rootClassName="transfer-mno-drawer-root"
      extra={
        <Button
          type="text"
          onClick={handleCloseOpenDrawer}
          icon={<CloseOutlined style={{ color: 'white' }} />}
          className="drawer-close-button"
        />
      }
    >
      {isDrawerOpen?.open ? (
        <div className="drawer-layout-container">
          {renderStepContent()}
          {renderFooter()}
        </div>
      ) : (
        <Flex
          justify="center"
          align="center"
          style={{ height: '100%' }}
        >
          <Spin size="large" />
        </Flex>
      )}
    </Drawer>
  );
};

export default TransferToMNOPage;
